import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { habitsApi } from '../services/habitsService'
import { habitsTransformers } from '../utils/habitsTransformers'
import { HabitsHookResult, HabitsState } from '../types/habits.types'

/**
 * Hook para obtener y gestionar datos de hábitos desde Contentful
 *
 * @returns {HabitsHookResult} Objeto con datos de hábitos y estados de la query
 *
 * @example
 * ```typescript
 * const { loading, allianceData, dimensionData, allHabitsDays } = useHabits()
 *
 * if (loading) return <div>Cargando hábitos...</div>
 *
 * return (
 *   <div>
 *     {allianceData && allianceData.map(item => (
 *       <div key={item.idReto}>{item.tituloGeneral}</div>
 *     ))}
 *   </div>
 * )
 * ```
 */
const useHabits = (): HabitsHookResult => {
	const {
		data: rawData,
		error,
		isLoading: loading,
		isError,
		refetch,
		isStale,
		isRefetching,
		dataUpdatedAt
	} = useQuery({
		queryKey: ['contentful', 'habits'],
		queryFn: habitsApi.getHabits,
		staleTime: 5 * 24 * 60 * 60 * 1000, // 5 días
		cacheTime: 5 * 24 * 60 * 60 * 1000, // 5 días
		retry: 2,
		retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
		refetchOnWindowFocus: false,
		refetchOnReconnect: true
	})

	// Log de errores
	if (isError && error) {
		console.error('❌ Error en useHabits:', error)
	}

	// Transformar datos usando useMemo para evitar recálculos innecesarios
	const transformedData = useMemo((): HabitsState => {
		if (!rawData) {
			return {
				allianceData: false,
				dimensionData: false,
				allHabitsDays: false
			}
		}

		return habitsTransformers.transformHabitsData(rawData)
	}, [rawData])

	// Información del cache
	const cacheInfo = useMemo(() => {
		const lastUpdated = new Date(dataUpdatedAt)
		const cacheExpiry = new Date(dataUpdatedAt + 5 * 24 * 60 * 60 * 1000) // 5 días

		return {
			cacheExpiry,
			lastUpdated
		}
	}, [dataUpdatedAt])

	return {
		loading,
		error: isError ? (error as Error) : null,
		allianceData: transformedData.allianceData,
		dimensionData: transformedData.dimensionData,
		allHabitsDays: transformedData.allHabitsDays,
		refetch,
		isStale,
		isRefetching,
		cacheInfo,
		rawData
	}
}

export default useHabits
